import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/hooks/useLanguage";
import { useHeroLoop } from "./hero/useHeroLoop";
import { NordicMapAnimation } from "./hero/NordicMapAnimation";

const BADGES = ["Adressvalidering", "Områdeshantering", "Adresstvätt"];

// Inner wrapper so the rAF loop only starts once the map is mounted (deferred
// past LCP). Keeps the hook call unconditional inside its own component.
function HeroMap() {
  const t = useHeroLoop(10000);
  return <NordicMapAnimation t={t} />;
}

export function HeroSection() {
  const { getLocalizedPath } = useLanguage();

  // Defer the map (and its animation loop) until the browser is idle, so the
  // copy/CTAs paint first and the hero doesn't block LCP.
  const [mapReady, setMapReady] = useState(false);
  useEffect(() => {
    if ("requestIdleCallback" in window) {
      const id = requestIdleCallback(() => setMapReady(true), { timeout: 800 });
      return () => cancelIdleCallback(id);
    }
    const id = setTimeout(() => setMapReady(true), 100);
    return () => clearTimeout(id);
  }, []);

  return (
    <section className="relative flex flex-col overflow-hidden bg-white font-body lg:flex-row">
      {/* Left: petrol copy panel */}
      <div className="relative w-full bg-primary px-6 py-16 text-white lg:w-1/2 lg:flex-[0_0_50%] lg:pb-14 lg:pl-[88px] lg:pr-14 lg:pt-[150px]">
        <div
          className="text-coral"
          style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.22em", textTransform: "uppercase", marginBottom: 18 }}
        >
          59°34'0"N · 18°01'0"E
        </div>
        <div className="bg-coral" style={{ width: 56, height: 1.5, marginBottom: 28 }} />
        <h1 className="m-0 text-[34px] font-bold leading-none tracking-[-0.035em] text-white [text-wrap:balance] sm:text-[44px] lg:text-[54px]">
          Geografisk kontroll
          <br />
          över din verksamhet.
        </h1>
        <p className="mt-7 max-w-[480px] text-[16px] font-light leading-[1.55] text-white/80 lg:text-[18px]">
          Validera adresser, hantera geografiska områden och kvalitetssäkra era
          adressregister i Sverige, Norge, Danmark och Finland — allt i en
          plattform. För smartare beslut och färre fel i varje led.
        </p>
        <div className="mt-11 flex flex-wrap items-center gap-[14px]">
          <Button
            asChild
            className="h-auto gap-2.5 rounded-full bg-coral px-[22px] py-[14px] text-[15px] font-semibold tracking-[0.01em] text-coral-foreground shadow-[0_8px_20px_hsl(var(--coral)/0.28)] hover:bg-coral/90"
          >
            <Link to={getLocalizedPath("/kontakt?type=demo")}>
              Boka demo
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </Button>
          <Button
            asChild
            variant="outline"
            className="h-auto rounded-full border-[1.5px] border-white/35 bg-transparent px-[22px] py-[14px] text-[15px] font-semibold tracking-[0.01em] text-white hover:bg-white/10 hover:text-white"
          >
            <Link to={getLocalizedPath("/losningar")}>Se våra lösningar</Link>
          </Button>
        </div>
      </div>

      {/* Right: light map panel */}
      <div className="relative min-h-[420px] flex-1 overflow-hidden bg-[#f8f8f8] lg:min-h-0">
        {/* Top-right product badges (desktop only) */}
        <div className="absolute left-10 right-10 top-10 z-10 hidden items-center justify-center gap-2.5 lg:flex">
          {BADGES.map((label) => (
            <span
              key={label}
              className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--petrol)/0.12)] bg-white py-2 pl-3 pr-3.5 text-[12px] font-semibold text-primary shadow-[0_2px_6px_hsl(var(--petrol)/0.06)]"
            >
              <span className="h-[7px] w-[7px] flex-[0_0_7px] rounded-full bg-coral" />
              {label}
            </span>
          ))}
        </div>

        {/* Map */}
        <div className="absolute inset-0 p-6 lg:px-10 lg:pb-14 lg:pt-[70px]">
          {mapReady && <HeroMap />}
        </div>

        {/* Bottom meta */}
        <div
          className="absolute bottom-7 left-0 right-0 text-center text-[#8c8d8d]"
          style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.22em" }}
        >
          BYGGD PÅ NORDENS MEST PÅLITLIGA ADRESS- &amp; POSTNUMMERDATA
        </div>
      </div>
    </section>
  );
}
